import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import questionsData from "@/data/questions.json";
import { ProgressBar } from "./ProgressBar";
import { QuestionCard, type OptionLike, type QuestionType } from "./QuestionCard";
import { ContactStep } from "./ContactStep";
import { FreeTextStep } from "./FreeTextStep";
import type { Lang, QualificationResult, WizardAnswers, ContactInfo } from "@/lib/types";
import { qualify } from "@/lib/rule-engine";
import { postQualify } from "@/lib/api";

type RawQuestion = {
  id: string;
  type: string;
  i18nKey: string;
  options?: OptionLike[];
  helpable?: boolean;
  default?: string[];
};

const QUESTIONS: RawQuestion[] = (questionsData as { questions: RawQuestion[] }).questions;

const STORAGE_KEY = "levelcare:wizard";

type AnswersDraft = {
  besoin?: string;
  nationalite?: string;
  situation?: string;
  duree_france?: string;
  profession?: string;
  evenements_passes?: string[];
  contact?: ContactInfo;
  free_text?: string;
};

const EMPTY_CONTACT: ContactInfo = {
  prenom: "",
  email: "",
  whatsapp: "",
  code_postal: "",
  rgpd_consent: false,
};

export interface WizardProps {
  onComplete: (result: QualificationResult, answers: WizardAnswers, submissionId: string | null) => void;
  source?: string;
}

export function Wizard({ onComplete, source }: WizardProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Lang;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<AnswersDraft>(() => loadDraft());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [contactErrors, setContactErrors] = useState<Partial<Record<keyof ContactInfo, string>>>({});

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
  }, [answers]);

  // Step layout: 6 questions (besoin..evenements) + contact (7) + free_text (8)
  const totalSteps = QUESTIONS.length;
  const current = QUESTIONS[step];

  const canGoNext = useMemo(() => {
    if (!current) return false;
    if (current.type === "contact_form") return validateContact(answers.contact ?? EMPTY_CONTACT) === null;
    if (current.type === "textarea") return true;
    if (current.type === "multi_checkbox") {
      const v = (answers as Record<string, unknown>)[current.id] as string[] | undefined;
      return Array.isArray(v) && v.length > 0;
    }
    const v = (answers as Record<string, unknown>)[current.id];
    return typeof v === "string" && v.length > 0;
  }, [current, answers]);

  const isLast = step === totalSteps - 1;

  const handleNext = async () => {
    if (current.type === "contact_form") {
      const err = validateContactDetailed(answers.contact ?? EMPTY_CONTACT, t);
      setContactErrors(err);
      if (Object.keys(err).length > 0) return;
    }
    if (!isLast) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    await submit();
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    const built: WizardAnswers = {
      besoin: answers.besoin as WizardAnswers["besoin"],
      nationalite: answers.nationalite || "OTHER",
      situation: answers.situation as WizardAnswers["situation"],
      duree_france: answers.duree_france as WizardAnswers["duree_france"],
      profession: answers.profession as WizardAnswers["profession"],
      evenements_passes: (answers.evenements_passes as WizardAnswers["evenements_passes"]) ?? ["non"],
      contact: answers.contact ?? EMPTY_CONTACT,
      free_text: answers.free_text || undefined,
    };

    const res = await postQualify(lang, built, source);
    setSubmitting(false);

    if (res.ok) {
      localStorage.removeItem(STORAGE_KEY);
      onComplete(res.result, built, res.submission_id);
      return;
    }

    // Fallback: client-side rule engine if backend is down. Naouil n'a pas de mail mais le user voit son résultat.
    const fallback = qualify(built);
    setSubmitError(res.error);
    onComplete(fallback, built, null);
  };

  const nextLabel = isLast ? t("wizard.submit") : t("wizard.next");

  return (
    <>
      <div className="mx-auto max-w-wizard px-4 pb-32 pt-6 md:px-0 md:pb-12 md:pt-12">
        <ProgressBar current={step + 1} total={totalSteps} />

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="mt-6 md:mt-8"
          >
            {renderStep(current, answers, setAnswers, lang, contactErrors)}
          </motion.div>
        </AnimatePresence>

        {submitError && (
          <p className="mt-4 text-caption text-danger">
            {t("errors.submit_failed")} ({submitError})
          </p>
        )}

        {/* Desktop footer: inline buttons under the card */}
        <div className="mt-6 hidden items-center justify-between md:flex">
          <button
            type="button"
            onClick={handleBack}
            disabled={step === 0 || submitting}
            className="btn-secondary px-4 py-2 disabled:opacity-30"
          >
            <ArrowLeft size={18} strokeWidth={1.75} />
            {t("wizard.back")}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoNext || submitting}
            className="btn-primary"
          >
            {submitting ? (
              <>
                <Loader2 size={18} strokeWidth={2} className="animate-spin" />
                {t("wizard.help_me_modal.loading")}
              </>
            ) : (
              <>
                {nextLabel}
                <ArrowRight size={18} strokeWidth={1.75} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Mobile sticky bottom action bar — the most important UX element on small screens */}
      <div className="bottom-action-bar">
        <div className="mx-auto flex max-w-wizard items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={handleBack}
            disabled={step === 0 || submitting}
            aria-label={t("wizard.back")}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-card border-[1.5px] border-ink text-ink transition-all duration-page hover:bg-ink/5 disabled:opacity-30"
          >
            <ArrowLeft size={20} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoNext || submitting}
            className="btn-primary flex-1 justify-center"
          >
            {submitting ? (
              <>
                <Loader2 size={20} strokeWidth={2} className="animate-spin" />
                {t("wizard.help_me_modal.loading")}
              </>
            ) : (
              <>
                {nextLabel}
                <ArrowRight size={20} strokeWidth={1.75} />
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function renderStep(
  q: RawQuestion,
  answers: AnswersDraft,
  setAnswers: React.Dispatch<React.SetStateAction<AnswersDraft>>,
  lang: Lang,
  contactErrors: Partial<Record<keyof ContactInfo, string>>,
) {
  if (q.type === "contact_form") {
    return (
      <ContactStep
        value={answers.contact ?? EMPTY_CONTACT}
        onChange={(v) => setAnswers((a) => ({ ...a, contact: v }))}
        errors={contactErrors}
      />
    );
  }
  if (q.type === "textarea") {
    return (
      <FreeTextStep
        value={answers.free_text ?? ""}
        onChange={(v) => setAnswers((a) => ({ ...a, free_text: v }))}
      />
    );
  }
  const value = (answers as Record<string, unknown>)[q.id];
  return (
    <QuestionCard
      questionId={q.id}
      type={q.type as QuestionType}
      i18nLabelKey={q.i18nKey}
      i18nHelpKey={q.id === "evenements_passes" ? q.i18nKey : undefined}
      options={q.options}
      helpable={q.helpable}
      lang={lang}
      value={
        q.type === "multi_checkbox"
          ? Array.isArray(value)
            ? value
            : (q.default ?? [])
          : typeof value === "string"
            ? value
            : ""
      }
      onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
    />
  );
}

function loadDraft(): AnswersDraft {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { evenements_passes: ["non"], contact: EMPTY_CONTACT };
    const parsed = JSON.parse(raw) as AnswersDraft;
    return { evenements_passes: ["non"], contact: EMPTY_CONTACT, ...parsed };
  } catch {
    return { evenements_passes: ["non"], contact: EMPTY_CONTACT };
  }
}

function validateContact(c: ContactInfo): string | null {
  if (!c.prenom.trim()) return "prenom";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c.email)) return "email";
  if (!/^[+\d\s]{8,}$/.test(c.whatsapp)) return "whatsapp";
  if (!/^\d{5}$/.test(c.code_postal)) return "code_postal";
  if (!c.rgpd_consent) return "rgpd_consent";
  return null;
}

function validateContactDetailed(
  c: ContactInfo,
  t: (k: string) => string,
): Partial<Record<keyof ContactInfo, string>> {
  const errs: Partial<Record<keyof ContactInfo, string>> = {};
  if (!c.prenom.trim()) errs.prenom = t("errors.required");
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c.email)) errs.email = t("errors.invalid_email");
  if (!/^[+\d\s]{8,}$/.test(c.whatsapp)) errs.whatsapp = t("errors.required");
  if (!/^\d{5}$/.test(c.code_postal)) errs.code_postal = t("errors.invalid_postal");
  if (!c.rgpd_consent) errs.rgpd_consent = t("errors.required");
  return errs;
}
